/** Shared slot semantics for Figma preset grids (home + PDP). */

export interface GridSlotPreset {
  sortOrder: number;
  labelAr: string;
  ratioLabel: string;
  hintPx: string;
  area: string;
  aspectRatio: string;
}

export const SECTION_TYPE = {
  PROMO_TRIPLE: 11,
  FEATURE_GRID_6: 12,
  PRODUCT_FEATURE_GRID: 14,
  /** Four dark “أقل من X جنيه” price-tier promo cards (Figma 538:20008). */
  PRICE_TIER_QUAD: 30,
} as const;

export const FEATURE_GRID_6_SLOTS: GridSlotPreset[] = [
  { sortOrder: 0, labelAr: 'يسار طولي', ratioLabel: '1:2', hintPx: '390×790', area: 'tall-left', aspectRatio: '390 / 790' },
  { sortOrder: 1, labelAr: 'وسط أعلى', ratioLabel: '1:1', hintPx: '385×385', area: 'mid-top', aspectRatio: '385 / 385' },
  { sortOrder: 2, labelAr: 'وسط أسفل', ratioLabel: '1:1', hintPx: '385×385', area: 'mid-bottom', aspectRatio: '385 / 385' },
  { sortOrder: 3, labelAr: 'يمين مربع', ratioLabel: '1:1', hintPx: '785×785', area: 'right', aspectRatio: '785 / 785' },
  { sortOrder: 4, labelAr: 'أسفل يسار', ratioLabel: '2:1', hintPx: '775×390', area: 'bottom-left', aspectRatio: '785 / 390' },
  { sortOrder: 5, labelAr: 'أسفل يمين', ratioLabel: '2:1', hintPx: '785×390', area: 'bottom-right', aspectRatio: '785 / 390' },
];

export const PROMO_TRIPLE_SLOTS: GridSlotPreset[] = [
  { sortOrder: 0, labelAr: 'بطاقة 1', ratioLabel: '1:1', hintPx: '625×625', area: 'main', aspectRatio: '1 / 1' },
  { sortOrder: 1, labelAr: 'بطاقة 2', ratioLabel: '1:1', hintPx: '625×625', area: 'side-1', aspectRatio: '1 / 1' },
  { sortOrder: 2, labelAr: 'بطاقة 3', ratioLabel: '1:1', hintPx: '625×625', area: 'side-2', aspectRatio: '1 / 1' },
];

/** Four horizontal price-tier cards: media left, copy right (badge/title/description). */
export const PRICE_TIER_QUAD_SLOTS: GridSlotPreset[] = [
  { sortOrder: 0, labelAr: 'بطاقة 1', ratioLabel: '385:160', hintPx: '385×160', area: 'tier-1', aspectRatio: '385 / 160' },
  { sortOrder: 1, labelAr: 'بطاقة 2', ratioLabel: '385:160', hintPx: '385×160', area: 'tier-2', aspectRatio: '385 / 160' },
  { sortOrder: 2, labelAr: 'بطاقة 3', ratioLabel: '385:160', hintPx: '385×160', area: 'tier-3', aspectRatio: '385 / 160' },
  { sortOrder: 3, labelAr: 'بطاقة 4', ratioLabel: '385:160', hintPx: '385×160', area: 'tier-4', aspectRatio: '385 / 160' },
];

/** Native pixel size of CMS landscape banners (upload at 2× this for retina). */
export const PRODUCT_FEATURE_BANNER_NATIVE = {
  width: 785,
  height: 390,
} as const;

export const PRODUCT_FEATURE_GRID_VARIANTS = {
  FOUR: 4,
  THREE: 3,
  TWO: 2,
} as const;

export function productFeatureSlotCount(variant: number): number {
  if (variant === PRODUCT_FEATURE_GRID_VARIANTS.TWO) return 2;
  if (variant === PRODUCT_FEATURE_GRID_VARIANTS.THREE) return 3;
  return 4;
}

export function getProductFeatureSlots(variant: number): GridSlotPreset[] {
  if (variant === PRODUCT_FEATURE_GRID_VARIANTS.TWO) {
    return [
      { sortOrder: 0, labelAr: 'يسار', ratioLabel: '785:390', hintPx: '785×390', area: 'pf-2-a', aspectRatio: '785 / 390' },
      { sortOrder: 1, labelAr: 'يمين', ratioLabel: '785:390', hintPx: '785×390', area: 'pf-2-b', aspectRatio: '785 / 390' },
    ];
  }
  if (variant === PRODUCT_FEATURE_GRID_VARIANTS.THREE) {
    return [
      { sortOrder: 0, labelAr: 'يسار عرضي', ratioLabel: '785:390', hintPx: '785×390', area: 'pf-3-a', aspectRatio: '785 / 390' },
      { sortOrder: 1, labelAr: 'يسار أسفل', ratioLabel: '785:390', hintPx: '785×390', area: 'pf-3-b', aspectRatio: '785 / 390' },
      { sortOrder: 2, labelAr: 'يمين', ratioLabel: '1:1', hintPx: '830×830', area: 'pf-3-c', aspectRatio: '1 / 1' },
    ];
  }
  return [
    { sortOrder: 0, labelAr: 'يسار طولي', ratioLabel: '1:2', hintPx: '420×840', area: 'pf-4-a', aspectRatio: '300 / 600' },
    { sortOrder: 1, labelAr: 'وسط أعلى', ratioLabel: '1:1', hintPx: '405×405', area: 'pf-4-b', aspectRatio: '1 / 1' },
    { sortOrder: 2, labelAr: 'وسط أسفل', ratioLabel: '1:1', hintPx: '405×405', area: 'pf-4-c', aspectRatio: '1 / 1' },
    { sortOrder: 3, labelAr: 'يمين', ratioLabel: '1:1', hintPx: '840×840', area: 'pf-4-d', aspectRatio: '1 / 1' },
  ];
}

export const DEFAULT_FIGMA_HOME_ASSETS = {
  promoTriple: [
    '/assets/marketing/figma/home/promo-smart-systems.jpg',
    '/assets/marketing/figma/home/promo-accessories.jpg',
    '/assets/marketing/figma/home/promo-speaker.jpg',
  ],
  featureGrid6: [
    '/assets/marketing/figma/home/grid-6-tall.jpg',
    '/assets/marketing/figma/home/grid-6-mid-top.jpg',
    '/assets/marketing/figma/home/grid-6-mid-bottom.jpg',
    '/assets/marketing/figma/home/grid-6-right.jpg',
    '/assets/marketing/figma/home/grid-6-bottom-left.jpg',
    '/assets/marketing/figma/home/grid-6-bottom-right.jpg',
  ],
  bannerWide: '/assets/marketing/figma/home/banner-pluto-tv.jpg',
};
